import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Activity, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";

const TONE = {
  high: { bar: "#E11D48", ring: "border-rose-200 bg-rose-50/50", chip: "bg-rose-50 text-rose-700 border-rose-200", icon: "text-rose-600" },
  moderate: { bar: "#F59E0B", ring: "border-amber-200 bg-amber-50/50", chip: "bg-amber-50 text-amber-700 border-amber-200", icon: "text-amber-600" },
  low: { bar: "#0EA5E9", ring: "border-slate-200 bg-white", chip: "bg-slate-50 text-slate-600 border-slate-200", icon: "text-[#003E87]" },
};

// Per-bid commodity exposure: how much material cost is tied to volatile indexes,
// and how far the frozen estimate sits from today's market.
export default function CommodityExposureCard({ bidId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!bidId) return;
    api.get(`/bids/${bidId}/commodity-exposure`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [bidId]);

  if (!data || !data.available) return null;
  const t = TONE[data.tone] || TONE.low;
  const delta = data.market_delta || 0;
  const up = delta > 0;
  const maxExposed = Math.max(...data.indexes.map((i) => i.exposed), 1);

  return (
    <Card className={`p-4 mb-4 border ${t.ring}`} data-testid="commodity-exposure-card">
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg bg-white border border-slate-200 grid place-items-center shrink-0 ${t.icon}`}>
          <Activity className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-slate-800">Commodity exposure</h3>
            <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 border ${t.chip}`} data-testid="commodity-exposure-pct">
              {data.exposure_pct}% of material index-linked
            </span>
            {data.tone === "high" && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-700"><AlertTriangle className="w-3 h-3" /> High exposure</span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            {money(data.exposed_amount)} of your material cost moves with commodity markets.
          </p>

          <div className="mt-2 flex items-center gap-2 text-xs" data-testid="commodity-market-delta">
            <span className="text-slate-500">vs today's market:</span>
            <span className={`inline-flex items-center gap-1 font-mono font-semibold ${up ? "text-rose-600" : delta < 0 ? "text-emerald-700" : "text-slate-500"}`}>
              {up ? <TrendingUp className="w-3.5 h-3.5" /> : delta < 0 ? <TrendingDown className="w-3.5 h-3.5" /> : null}
              {up ? "+" : ""}{money(delta)} ({up ? "+" : ""}{data.market_delta_pct}%)
            </span>
            <span className="text-slate-400">{up ? "market has risen above your frozen prices" : delta < 0 ? "market has fallen below your frozen prices" : "in line with your frozen prices"}</span>
          </div>

          <div className="mt-3 space-y-1.5">
            {data.indexes.map((ix) => (
              <div key={ix.index_id} className="flex items-center gap-2" data-testid={`commodity-index-${ix.index_id}`}>
                <div className="w-28 shrink-0 truncate text-xs text-slate-600" title={ix.name}>{ix.name}</div>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.max(4, (ix.exposed / maxExposed) * 100)}%`, backgroundColor: t.bar }} />
                </div>
                <div className="w-20 shrink-0 text-right font-mono text-xs text-slate-700">{money(ix.exposed)}</div>
                <span className={`w-14 shrink-0 text-right text-[10px] font-mono ${(ix.change_pct || 0) > 0 ? "text-rose-600" : (ix.change_pct || 0) < 0 ? "text-emerald-600" : "text-slate-400"}`}>
                  {ix.manual ? "manual" : `${(ix.change_pct || 0) > 0 ? "+" : ""}${(ix.change_pct || 0).toFixed(1)}%`}
                </span>
              </div>
            ))}
          </div>

          {data.movers && data.movers.length > 0 && (
            <div className="mt-2 text-[11px] text-slate-500" data-testid="commodity-movers">
              Moving now: {data.movers.map((m) => `${m.name} ${m.change_pct > 0 ? "+" : ""}${m.change_pct.toFixed(1)}%`).join(" · ")}
            </div>
          )}
          {data.can_reprice ? (
            <div className="mt-2 text-[11px] text-[#003E87]">This is a revision — use <b>Reprice to index</b> (top bar) to move these lines to today's rates.</div>
          ) : (
            <div className="mt-2 text-[11px] text-slate-400">Repricing to live market rates is available on a revision of this bid.</div>
          )}
        </div>
      </div>
    </Card>
  );
}
